import type { BadgeTone } from '@/lib/utils/badgeTone'

import { validateReservationPeriod } from './reservationViewModel'
import type {
  VenueSettlementCycleStatus,
  VenueSettlementDeductionsView,
  VenueSettlementRevenueCycleAxis,
} from './settlementTypes'

/**
 * 매출·정산 화면 공통 순수 함수(설계 §2) — 프리셋/범위 검증·쿼리 파라미터 조립·상태 톤·안내 문구.
 * `reservationViewModel.ts`와 같은 규칙 — `if (status === ...)`를 컴포넌트에 흩지 않는다.
 *
 * 🚨 `revenue`/`settlement` 두 블록을 합산하는 함수는 이 파일에 두지 않는다(api-spec §1 —
 * 두 축의 숫자를 더하거나 비교하지 않는다는 규약을 "만들 수 있는 코드가 없음"으로 강제한다).
 */

// ---------------------------------------------------------------------------
// 매출 현황(기간 축) — `GET .../settlements/revenue`
// ---------------------------------------------------------------------------

export interface SettlementRevenueFilter {
  preset: 'THIS_MONTH' | 'LAST_MONTH' | 'CUSTOM'
  /** `preset==='CUSTOM'`일 때만 쓴다. */
  from: string
  to: string
}

export function defaultRevenueFilter(): SettlementRevenueFilter {
  return { preset: 'THIS_MONTH', from: '', to: '' }
}

export const REVENUE_PRESET_OPTIONS: { value: SettlementRevenueFilter['preset']; label: string }[] = [
  { value: 'THIS_MONTH', label: '이번 달' },
  { value: 'LAST_MONTH', label: '지난 달' },
  { value: 'CUSTOM', label: '직접 지정' },
]

/**
 * 🚨 `preset` 해석은 서버가 한다(api-spec §2). 클라이언트는 `CUSTOM`일 때만 `from`/`to`를 보내고,
 * 그 외에는 `preset`만 보내 서버가 KST 기준으로 계산하게 한다(자체 계산 시 UTC 경계 버그 재현 위험).
 */
export function settlementRevenueQueryParams(
  filter: SettlementRevenueFilter
): Record<string, string | undefined> {
  if (filter.preset !== 'CUSTOM') return { preset: filter.preset }
  return { preset: filter.preset, from: filter.from, to: filter.to }
}

/** `CUSTOM`일 때만 검증 — 나머지 프리셋은 서버가 계산하므로 클라이언트가 막을 입력이 없다. */
export function validateRevenuePeriod(
  filter: Pick<SettlementRevenueFilter, 'preset' | 'from' | 'to'>
): string | null {
  if (filter.preset !== 'CUSTOM') return null
  return validateReservationPeriod({ from: filter.from, to: filter.to })
}

/**
 * `partialCycleIncluded=true`일 때의 안내 — api-spec 체크리스트 "회차 전체 금액임을 표시한다".
 * `false`면 `null`(빈 문구를 렌더하지 않는다).
 */
export function partialCycleNoticeText(
  settlement: Pick<VenueSettlementRevenueCycleAxis, 'partialCycleIncluded'>
): string | null {
  if (!settlement.partialCycleIncluded) return null
  return '선택한 기간이 정산 회차 일부만 포함해도, 정산 금액은 그 회차 전체 기준으로 집계됩니다.'
}

/**
 * `confirmedAmount=0`이 정상 상태임을 설명 — api-spec 체크리스트 "오류로 그리지 않는다".
 * 마감 배치가 아직 안 닫은 회차만 있으면(`estimatedCycleCount>0`) 안내를 보여준다.
 */
export function confirmedAmountNoticeText(
  settlement: Pick<VenueSettlementRevenueCycleAxis, 'confirmedAmount' | 'estimatedCycleCount'>
): string | null {
  if (settlement.confirmedAmount !== 0 || settlement.estimatedCycleCount === 0) return null
  return '아직 마감되지 않은 회차라 확정 금액이 0원으로 표시됩니다. 예정 금액은 다음 정산 카드에서 확인하세요.'
}

// ---------------------------------------------------------------------------
// 정산 회차 목록 — `GET .../settlements/cycles`
// ---------------------------------------------------------------------------

export interface SettlementCyclesFilter {
  /** "YYYY-MM". 비우면 서버 기본값(다음 정산 회차부터 과거 6회차)을 쓴다. */
  fromMonth: string
  toMonth: string
  /** 빈 배열 = 전체 상태. */
  statuses: VenueSettlementCycleStatus[]
}

export function defaultCyclesFilter(): SettlementCyclesFilter {
  return { fromMonth: '', toMonth: '', statuses: [] }
}

export const SETTLEMENT_CYCLE_STATUS_OPTIONS: { value: VenueSettlementCycleStatus; label: string }[] = [
  { value: 'PENDING', label: '정산 예정' },
  { value: 'CONFIRMED', label: '확정' },
  { value: 'PAID', label: '지급 완료' },
]

export const SETTLEMENT_CYCLE_STATUS_TONE: Record<VenueSettlementCycleStatus, BadgeTone> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  PAID: 'neutral',
}

export function settlementCyclesQueryParams(
  filter: SettlementCyclesFilter,
  cursor?: string
): Record<string, string | undefined> {
  return {
    fromMonth: filter.fromMonth || undefined,
    toMonth: filter.toMonth || undefined,
    statuses: filter.statuses.length > 0 ? filter.statuses.join(',') : undefined,
    // 🚨 커서는 회차월(`YYYY-MM`) 그 자체다 — 서버가 준 값을 그대로 되싣는다(가공 금지).
    cursor,
  }
}

const MAX_CYCLE_RANGE_MONTHS = 36

function cycleMonthIndex(yearMonth: string): number {
  const [year = 0, month = 1] = yearMonth.split('-').map(Number)
  return year * 12 + (month - 1)
}

/**
 * 회차 범위 사전 검증(api-spec §3) — 역전·36회차 초과는 서버가 400(보정하지 않는다).
 * 두 값이 모두 있을 때만 검증한다 — 비워두면 서버 기본 범위를 쓴다.
 */
export function validateCyclesRange(range: Pick<SettlementCyclesFilter, 'fromMonth' | 'toMonth'>): string | null {
  if (!range.fromMonth || !range.toMonth) return null
  if (range.toMonth < range.fromMonth) return '종료 회차는 시작 회차와 같거나 이후여야 합니다.'
  const span = cycleMonthIndex(range.toMonth) - cycleMonthIndex(range.fromMonth) + 1
  if (span > MAX_CYCLE_RANGE_MONTHS) {
    return `조회 범위는 최대 ${MAX_CYCLE_RANGE_MONTHS}회차까지 선택할 수 있습니다.`
  }
  return null
}

/** 정산 회차 CSV 다운로드 파일명 — api-spec §6 패턴을 그대로 재현한다. */
export function settlementCyclesCsvFileName(venueId: string, fromMonth: string, toMonth: string): string {
  return `settlements_${venueId}_${fromMonth}_${toMonth}.csv`
}

/**
 * 매출 현황(기간 축) 화면의 CSV 다운로드는 회차 CSV EP(fromMonth/toMonth)를 재사용한다 —
 * api-spec §6 이 정산 회차·환불 결과 2본만 정의하므로, 조회 중인 기간이 걸친 회차 범위로 변환한다.
 * `cycleMonths`는 항상 오름차순이 아닐 수 있어 방어적으로 정렬 후 양끝을 취한다.
 */
export function cycleExportRangeFrom(cycleMonths: string[]): { fromMonth: string; toMonth: string } {
  const sorted = [...cycleMonths].sort()
  return { fromMonth: sorted[0] ?? '', toMonth: sorted.at(-1) ?? '' }
}

// ---------------------------------------------------------------------------
// 분쟁환불 차감 내역 — `GET .../settlements/deductions`
// ---------------------------------------------------------------------------

export interface SettlementDeductionsFilter {
  /** "YYYY-MM". 비우면 서버 기본값(다음 정산 회차)을 쓴다. */
  cycleMonth: string
}

export function defaultDeductionsFilter(): SettlementDeductionsFilter {
  return { cycleMonth: '' }
}

export function settlementDeductionsQueryParams(
  filter: SettlementDeductionsFilter,
  cursor?: string
): Record<string, string | undefined> {
  return { cycleMonth: filter.cycleMonth || undefined, cursor }
}

/**
 * `overDeducted=true`일 때의 음수 안내 — api-spec §4 "회수해야 할 금액이 장부에서 사라지지 않게".
 * `settlementAmount`는 절대 0으로 뭉개지 않고 그대로 표시하되, 이 문구로 상황을 설명한다.
 */
export function overDeductedNoticeText(
  view: Pick<VenueSettlementDeductionsView, 'overDeducted'>
): string | null {
  if (!view.overDeducted) return null
  return '차감 금액이 이번 회차 정산 예정 금액을 초과해, 남은 금액은 다음 정산에서 이어서 차감됩니다.'
}

/** 분쟁환불 차감 적용 기준 고정 안내 문구(요구사항 명시 — 이미 확정·지급된 정산은 변경하지 않는다). */
export const DEDUCTION_POLICY_NOTICE =
  '이미 확정·지급된 정산은 바뀌지 않고, 이용 완료 후 확정된 분쟁·부분환불은 다음 정산 예정 금액에서 차감됩니다.'
